import {
  AppPages,
  PortalConfigurationType,
  UserProfile,
  VendorContactRole,
  VendorProfile,
} from '@trnsact/trnsact-shared-types';
import { CommonDataContext } from 'contexts/CommonDataContext';
import { usePortalConfigurationChecks } from 'modules/portalConfigurations';
import { useContext } from 'react';
import { useSelector } from 'react-redux';
import { portalConfigurationsSelectors } from 'redux/portalConfigurationReducer';
import { PortalConfigurationRolesSettingsKeys } from 'types';

export function useRoutesVisibilityChecks() {
  const { isDealerUser, isLenderUser, isCommonPartner, isAftermarketPartner, isSuperAdminOrSingleAccount } =
    useContext(CommonDataContext);

  const vendorProfile = useSelector((state: any) => state.vp) as VendorProfile;
  const userProfile = useSelector((state: any) => state.userProfile) as UserProfile;
  const portalConfigurationData = useSelector((state: any) => portalConfigurationsSelectors.configData(state));
  const isUserHaveAccessToPrescreenPage = useSelector((state: any) =>
    portalConfigurationsSelectors.checkIfUserHaveAccessToPrescreenPage(state, userProfile)
  );

  const {
    checkPortalConfigEntryForCurrentUserRole,
    checkAftermarketPartnerAllowSendProposals,
    checkRoutingAccessForCurrentUserRole,
  } = usePortalConfigurationChecks();

  const byUserRole = (userProfile: UserProfile, userRole: VendorContactRole) =>
    userProfile.vendorContactRole === userRole;

  const checkIfOpportunitiesVisible = (
    isAftermarketPartner: boolean,
    isDealerUser: boolean,
    isLenderUser: boolean,
    vendorProfile: VendorProfile
  ) => {
    if (isAftermarketPartner) {
      return checkAftermarketPartnerAllowSendProposals();
    }
    if (isDealerUser) {
      return !checkRoutingAccessForCurrentUserRole(AppPages.Opportunities);
    }
    if (isLenderUser) {
      return !!vendorProfile;
    }
    return false;
  };

  const checkIfSubmissionsHubIsVisible = (isDealerUser: boolean, isSuperAdminOrSingleAccount: boolean) => {
    if (isSuperAdminOrSingleAccount) {
      return true;
    }

    if (isDealerUser) {
      return !checkRoutingAccessForCurrentUserRole(AppPages.SubmissionsHub);
    }

    return false;
  };

  const checkIfCustomersVisible = (isDealerUser: boolean) => {
    if (isDealerUser) {
      return !checkRoutingAccessForCurrentUserRole(AppPages.Customers);
    }

    return false;
  };

  const checkIfProposalsHubVisible = (
    isCommonPartner: boolean,
    isAftermarketPartner: boolean,
    isDealerUser: boolean,
    isLenderUser: boolean,
    vendorProfile: VendorProfile
  ) => {
    if (isCommonPartner || isAftermarketPartner) {
      return false;
    }

    return checkIfOpportunitiesVisible(isAftermarketPartner, isDealerUser, isLenderUser, vendorProfile);
  };

  const checkIfCompanyProfileAvailable = () => {
    const hiddenByRoutingAccess = checkRoutingAccessForCurrentUserRole(AppPages.CompanyProfile);
    return (isDealerUser || isLenderUser || isAftermarketPartner) && !hiddenByRoutingAccess;
  };

  const checkIfUserManagementAvailable = () => {
    if (isSuperAdminOrSingleAccount) {
      return true;
    }

    if (isDealerUser) {
      const hiddenByPcw = checkPortalConfigEntryForCurrentUserRole(
        PortalConfigurationType.UserManagement,
        PortalConfigurationRolesSettingsKeys.Hidden
      );

      const hiddenByRoutingAccess = checkRoutingAccessForCurrentUserRole(AppPages.UserManagement);

      return !hiddenByPcw && !hiddenByRoutingAccess;
    }
    return true;
  };

  const checkIfLenderManagementAvailable = () => {
    if (isCommonPartner) {
      return false;
    }
    if (isDealerUser) {
      const hiddenByRoutingAccess = checkRoutingAccessForCurrentUserRole(AppPages.LenderManagement);
      if (hiddenByRoutingAccess) {
        return false;
      }

      if (!vendorProfile?.showLenderSubmission) {
        return false;
      }
      switch (vendorProfile?.showLenderSubmission) {
        case 'no_access':
          return false;
        case 'credit_manager_only':
          return byUserRole(userProfile, VendorContactRole.CreditMgr);
        case 'all_managers':
          return !byUserRole(userProfile, VendorContactRole.SalesRep);
        default:
          return !byUserRole(userProfile, VendorContactRole.SalesRep);
      }
    }
    if (isLenderUser) {
      return portalConfigurationData?.[PortalConfigurationType.Portal]?.jsonDefinition?.config?.allowSyndication;
    }
  };

  const checkIfFinanceProgramsAvailable = () => {
    const hiddenByRoutingAccess = checkRoutingAccessForCurrentUserRole(AppPages.FinancePrograms);
    return (isDealerUser || isLenderUser) && !hiddenByRoutingAccess;
  };

  const checkIfLocationManagementAvailable = () => {
    if (isAftermarketPartner) {
      return false;
    }

    const hiddenByRoutingAccess = checkRoutingAccessForCurrentUserRole(AppPages.LocationManagement);
    if (hiddenByRoutingAccess) {
      return false;
    }

    return !!vendorProfile?.multiLocationManagement;
  };

  const checkIfPortalSetupAvailable = () => {
    if (isDealerUser && !isCommonPartner && !isAftermarketPartner) {
      if (isSuperAdminOrSingleAccount) {
        return true;
      }

      const hiddenByPcw = checkPortalConfigEntryForCurrentUserRole(
        PortalConfigurationType.PortalSetup,
        PortalConfigurationRolesSettingsKeys.Hidden
      );
      const hiddenByRoutingAccess = checkRoutingAccessForCurrentUserRole(AppPages.PortalSetup);
      if (hiddenByPcw || hiddenByRoutingAccess) {
        return false;
      }

      return true;
    }

    return false;
  };

  const checkIfAftermarketProductsAvailable = () => {
    if (isAftermarketPartner) return true;

    if (isCommonPartner) return false;

    if (isDealerUser) {
      const portalConfiguration = portalConfigurationData?.[PortalConfigurationType.Aftermarket];

      const hiddenByRoutingAccess = checkRoutingAccessForCurrentUserRole(AppPages.AftermarketProducts);
      if (hiddenByRoutingAccess) return isSuperAdminOrSingleAccount;

      if (portalConfiguration) {
        return (
          isSuperAdminOrSingleAccount ||
          checkPortalConfigEntryForCurrentUserRole(
            PortalConfigurationType.Aftermarket,
            PortalConfigurationRolesSettingsKeys.AssignedOnly
          ) ||
          checkPortalConfigEntryForCurrentUserRole(
            PortalConfigurationType.Aftermarket,
            PortalConfigurationRolesSettingsKeys.ReadOnly
          )
        );
      }

      return true;
    }

    return false;
  };

  const checkIfInventoryAvailable = () => {
    if (isDealerUser) {
      const hiddenByRoutingAccess = checkRoutingAccessForCurrentUserRole(AppPages.Inventory);
      if (hiddenByRoutingAccess) {
        return false;
      }

      const portalConfiguration = portalConfigurationData?.[PortalConfigurationType.Inventory];

      if (portalConfiguration) {
        return (
          checkPortalConfigEntryForCurrentUserRole(
            PortalConfigurationType.Inventory,
            PortalConfigurationRolesSettingsKeys.AssignedOnly
          ) ||
          checkPortalConfigEntryForCurrentUserRole(
            PortalConfigurationType.Inventory,
            PortalConfigurationRolesSettingsKeys.ReadOnly
          )
        );
      }
    }

    return false;
  };

  const checkIfWebsiteIntegrationsAvailable = () => {
    if (isCommonPartner || isAftermarketPartner) {
      return false;
    }
    if (isDealerUser) {
      const hiddenByRoutingAccess = checkRoutingAccessForCurrentUserRole(AppPages.WebsiteIntegrations);
      if (hiddenByRoutingAccess) {
        return false;
      }
      return true;
    }
    return false;
  };

  const checkIfPrescreenToolsAvailable = () => {
    if (isCommonPartner || isAftermarketPartner) {
      return false;
    }

    if (isDealerUser) {
      const hiddenByRoutingAccess = checkRoutingAccessForCurrentUserRole(AppPages.PrescreenTools);
      if (hiddenByRoutingAccess) {
        return false;
      }

      return isUserHaveAccessToPrescreenPage;
    }
    if (isLenderUser) {
      const allowSyndication =
        portalConfigurationData?.[PortalConfigurationType.Portal]?.jsonDefinition?.config?.allowSyndication;

      return isUserHaveAccessToPrescreenPage && allowSyndication;
    }

    return false;
  };

  const checkIfDashboardVisible = () => {
    if (isDealerUser) return !checkRoutingAccessForCurrentUserRole(AppPages.Dashboard);
    return isLenderUser || isCommonPartner || isAftermarketPartner;
  };

  const checkIfFinanceVisible = () => {
    if (!isDealerUser) return false;
    return !checkRoutingAccessForCurrentUserRole(AppPages.Finance);
  };

  const checkIfStartDealVisible = () => {
    if (isLenderUser) return !!vendorProfile;
    if (isAftermarketPartner) return checkAftermarketPartnerAllowSendProposals();
    if (isDealerUser) return !checkRoutingAccessForCurrentUserRole(AppPages.StartDeal);
    return true;
  };

  const checkIfSendApplicationVisible = () => {
    if (isCommonPartner || isAftermarketPartner) return false;
    if (isLenderUser) return !!vendorProfile;
    if (isDealerUser) return !checkRoutingAccessForCurrentUserRole(AppPages.SendApplication);
    return true;
  };

  const checkIfLeadsVisible = () => {
    if (isAftermarketPartner) return false;
    if (isLenderUser) return !!vendorProfile;
    if (isDealerUser) return !checkRoutingAccessForCurrentUserRole(AppPages.Leads);
    return false;
  };

  const checkIfReportsVisible = () => {
    if (isCommonPartner || isAftermarketPartner) return false;
    if (isDealerUser) return !checkRoutingAccessForCurrentUserRole(AppPages.Reports);
    return isLenderUser;
  };

  const checkIfTasksVisible = () => {
    if (!isDealerUser || vendorProfile?.showTasks !== 'show') return false;
    return !checkRoutingAccessForCurrentUserRole(AppPages.Tasks);
  };

  const checkIfDocsVisible = () => {
    if (!isDealerUser || vendorProfile?.docusignDocMgmt !== 'show') return false;
    return !checkRoutingAccessForCurrentUserRole(AppPages.Docs);
  };

  const routeAvailabilityChecker: Record<AppPages, () => boolean> = {
    [AppPages.CompanyProfile]: checkIfCompanyProfileAvailable,
    [AppPages.UserManagement]: checkIfUserManagementAvailable,
    [AppPages.LenderManagement]: checkIfLenderManagementAvailable,
    [AppPages.FinancePrograms]: checkIfFinanceProgramsAvailable,
    [AppPages.LocationManagement]: checkIfLocationManagementAvailable,
    [AppPages.PortalSetup]: checkIfPortalSetupAvailable,
    [AppPages.AftermarketProducts]: checkIfAftermarketProductsAvailable,
    [AppPages.Inventory]: checkIfInventoryAvailable,
    [AppPages.WebsiteIntegrations]: checkIfWebsiteIntegrationsAvailable,
    [AppPages.PrescreenTools]: checkIfPrescreenToolsAvailable,
    [AppPages.Dashboard]: checkIfDashboardVisible,
    [AppPages.Opportunities]: () =>
      checkIfOpportunitiesVisible(isAftermarketPartner, isDealerUser, isLenderUser, vendorProfile),
    [AppPages.Finance]: checkIfFinanceVisible,
    [AppPages.SubmissionsHub]: () => checkIfSubmissionsHubIsVisible(isDealerUser, isSuperAdminOrSingleAccount),
    [AppPages.Customers]: () => checkIfCustomersVisible(isDealerUser),
    [AppPages.StartDeal]: checkIfStartDealVisible,
    [AppPages.SendApplication]: checkIfSendApplicationVisible,
    [AppPages.Leads]: checkIfLeadsVisible,
    [AppPages.Reports]: checkIfReportsVisible,
    [AppPages.Tasks]: checkIfTasksVisible,
    [AppPages.Docs]: checkIfDocsVisible,
  };

  return {
    checkIfOpportunitiesVisible,
    checkIfSubmissionsHubIsVisible,
    checkIfCustomersVisible,
    checkIfProposalsHubVisible,
    checkIfFinanceVisible,
    checkIfTasksVisible,
    routeAvailabilityChecker,
  };
}
